import React, { useRef } from 'react';
import { ArrowRight, ChevronDown, Zap, Cpu, Sparkles } from 'lucide-react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { trackCalendlyClick } from '../utils/analytics';
import TypingRotator from './TypingRotator';
import AtomScene from './AtomScene';
import { EASING, useMagneticHover, useReducedMotion } from '../hooks/useMotion';

const TRUST_WIDGETS = [
  { icon: Zap, label: 'Delivery', value: '< 30 days' },
  { icon: Cpu, label: 'Status', value: 'Fully Autonomous' },
  { icon: Sparkles, label: 'Stack', value: 'GPT-4o · Claude · Gemini' },
];

const headlineWords = ['Production-Grade', 'AI'];

const MagneticCTA = ({ children, ...rest }) => {
  const m = useMagneticHover(0.25);
  return (
    <motion.a
      ref={m.ref}
      onMouseMove={m.onMouseMove}
      onMouseLeave={m.onMouseLeave}
      animate={{ x: m.x, y: m.y }}
      transition={{ type: 'spring', stiffness: 220, damping: 18, mass: 0.6 }}
      {...rest}
    >
      {children}
    </motion.a>
  );
};

const HeroSection = () => {
  const containerRef = useRef(null);
  const reduced = useReducedMotion();

  // Scroll-bound transforms — the "fly-through" effect
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  // Atom: grows + drifts back, then fades out (camera flies through it)
  const atomScale = useTransform(scrollYProgress, [0, 0.6], reduced ? [1, 1] : [1, 2.4]);
  const atomOpacity = useTransform(scrollYProgress, [0, 0.4, 0.8], reduced ? [1, 1, 1] : [1, 0.7, 0]);
  const atomBlur = useTransform(scrollYProgress, [0, 0.5, 0.85], reduced ? [0, 0, 0] : [0, 4, 16]);
  const atomFilter = useTransform(atomBlur, (v) => `blur(${v}px)`);

  // Headline: scales down, blurs, fades while user scrolls
  const headlineScale = useTransform(scrollYProgress, [0, 0.5], reduced ? [1, 1] : [1, 0.78]);
  const headlineOpacity = useTransform(scrollYProgress, [0, 0.45], reduced ? [1, 1] : [1, 0]);
  const headlineY = useTransform(scrollYProgress, [0, 0.5], reduced ? [0, 0] : [0, -60]);
  const headlineBlurRaw = useTransform(scrollYProgress, [0, 0.5], reduced ? [0, 0] : [0, 8]);
  const headlineFilter = useTransform(headlineBlurRaw, (v) => `blur(${v}px)`);

  // Trust widgets sink slightly + fade
  const widgetsY = useTransform(scrollYProgress, [0, 0.6], reduced ? [0, 0] : [0, 80]);
  const widgetsOpacity = useTransform(scrollYProgress, [0, 0.55], reduced ? [1, 1] : [1, 0]);

  // Vignette intensifies as we scroll for the "tunnel" feel
  const vignetteOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 1.4]);

  // Smooth springs for premium feel
  const sAtomScale = useSpring(atomScale, { stiffness: 90, damping: 26, mass: 0.6 });
  const sAtomOpacity = useSpring(atomOpacity, { stiffness: 90, damping: 26, mass: 0.6 });

  const scrollToNext = () => {
    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      ref={containerRef}
      id="hero"
      className="relative min-h-screen overflow-hidden bg-[#050505] text-white"
      style={{ fontFamily: 'Manrope, sans-serif' }}
      data-testid="hero-section"
    >
      {/* Layer 1: Technical blueprint grid */}
      <div
        className="absolute inset-0 opacity-60 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle at 8px 8px, rgba(255,255,255,0.06) 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
        aria-hidden="true"
      />

      {/* Layer 2: Radial accent glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle at center, rgba(255,107,0,0.18) 0%, rgba(255,107,0,0.05) 30%, transparent 65%)',
          filter: 'blur(40px)',
        }}
        aria-hidden="true"
      />

      {/* Layer 3: Atom — scroll-bound fly-through */}
      <motion.div
        style={{
          scale: sAtomScale,
          opacity: sAtomOpacity,
          filter: atomFilter,
          willChange: 'transform, opacity, filter',
        }}
        className="absolute inset-0"
      >
        <AtomScene />
      </motion.div>

      {/* Layer 4: Vignette */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 30%, rgba(5,5,5,0.85) 80%)',
          opacity: vignetteOpacity,
        }}
        aria-hidden="true"
      />

      {/* Layer 5: Foreground content (sticky so motion stays visible across scroll) */}
      <div className="sticky top-0 z-10 flex flex-col items-center justify-center min-h-screen px-6 pt-28 pb-12">
        <div className="text-center max-w-6xl mx-auto">
          {/* Overline */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASING.appleSmooth }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/5 backdrop-blur-md mb-8"
            data-testid="hero-overline"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B00] animate-pulse" />
            <span
              className="text-[10px] tracking-[0.3em] uppercase text-[#FF6B00]"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              Agentic Atoms · Live System
            </span>
          </motion.div>

          {/* Headline — scroll-bound scale + blur + fade */}
          <motion.h1
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
            }}
            style={{
              scale: headlineScale,
              opacity: headlineOpacity,
              y: headlineY,
              filter: headlineFilter,
              willChange: 'transform, opacity, filter',
            }}
            className="text-5xl sm:text-6xl md:text-7xl lg:text-[6.5rem] font-bold leading-[0.92] tracking-tighter mb-6"
            data-testid="hero-headline"
          >
            <motion.span
              variants={{
                hidden: { y: '110%', opacity: 0 },
                visible: { y: 0, opacity: 1, transition: { duration: 0.85, ease: EASING.cinematicSlow } },
              }}
              className="block overflow-hidden"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              <span className="block text-white">We Build</span>
            </motion.span>
            {headlineWords.map((word, i) => (
              <motion.span
                key={word}
                variants={{
                  hidden: { y: '110%', opacity: 0 },
                  visible: { y: 0, opacity: 1, transition: { duration: 0.9, ease: EASING.cinematicSlow } },
                }}
                className="block overflow-hidden"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                <span
                  className="inline-block bg-clip-text text-transparent"
                  style={{
                    backgroundImage:
                      i === 1
                        ? 'linear-gradient(90deg, #FF6B00 0%, #FFB400 50%, #FF6B00 100%)'
                        : 'linear-gradient(180deg, #FFFFFF 0%, #A1A1AA 100%)',
                  }}
                >
                  {word}
                </span>
              </motion.span>
            ))}
            <motion.span
              variants={{
                hidden: { y: '110%', opacity: 0 },
                visible: { y: 0, opacity: 1, transition: { duration: 0.85, ease: EASING.cinematicSlow } },
              }}
              className="block overflow-hidden pb-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              <span className="inline-flex items-baseline justify-center gap-3 text-white/70">
                <span className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white/40 tracking-[0.15em]">for</span>
                <span className="inline-block text-white whitespace-nowrap">
                  <TypingRotator words={['Chatbots', 'Voice Agents', 'Receptionists', 'Sales Agents', 'Websites']} />
                </span>
              </span>
            </motion.span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: EASING.appleSmooth }}
            style={{ opacity: headlineOpacity }}
            className="text-base md:text-lg text-[#A1A1AA] max-w-xl mx-auto mb-10 leading-relaxed"
            data-testid="hero-subtitle"
          >
            Custom AI chatbots, voice agents, and automation for local businesses.
            <span className="text-white/85"> Shipped in 30 days.</span>
          </motion.p>

          {/* CTA cluster — magnetic */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85, ease: EASING.appleSmooth }}
            style={{ opacity: headlineOpacity }}
            className="flex flex-col sm:flex-row items-center justify-center gap-5 mb-16"
          >
            <MagneticCTA
              href="https://calendly.com/agenticatoms/30min"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCalendlyClick('hero_section')}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              className="group relative inline-flex items-center gap-3 px-8 py-4 rounded-lg bg-white text-black font-semibold text-base overflow-hidden shadow-[0_0_60px_rgba(255,107,0,0.25)] hover:shadow-[0_0_90px_rgba(255,107,0,0.55)] transition-shadow"
              data-testid="hero-cta-primary"
            >
              <span className="relative z-10">Book a Free AI Discovery Call</span>
              <ArrowRight className="w-4 h-4 relative z-10 group-hover:translate-x-1 transition-transform duration-300" />
              {/* Tracing beam */}
              <span className="absolute inset-0 bg-gradient-to-r from-[#FF6B00]/0 via-[#FF6B00]/40 to-[#FF6B00]/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            </MagneticCTA>
            <motion.button
              onClick={scrollToNext}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-white/5 backdrop-blur-md border border-white/10 text-white hover:bg-white/10 hover:border-[#FF6B00]/40 transition-all text-base font-medium"
              data-testid="hero-cta-secondary"
            >
              See How It Works
              <ArrowRight className="w-4 h-4" />
            </motion.button>
          </motion.div>

          {/* Floating glass trust widgets */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: EASING.appleSmooth }}
            style={{ y: widgetsY, opacity: widgetsOpacity }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-4xl mx-auto"
          >
            {TRUST_WIDGETS.map((w, i) => (
              <motion.div
                key={w.label}
                whileHover={{ y: -4, borderColor: 'rgba(255,107,0,0.4)' }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                className="relative bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-xl px-5 py-4 flex items-center gap-3 text-left"
                data-testid={`hero-trust-${i}`}
              >
                <div className="w-10 h-10 rounded-lg bg-[#FF6B00]/10 border border-[#FF6B00]/20 flex items-center justify-center shrink-0">
                  <w.icon className="w-4 h-4 text-[#FF6B00]" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#71717A]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {w.label}
                  </p>
                  <p className="text-sm font-semibold text-white truncate">{w.value}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          style={{ opacity: widgetsOpacity }}
          onClick={scrollToNext}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 hidden sm:block"
          aria-label="Scroll to services"
          data-testid="hero-scroll-indicator"
        >
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }} className="p-2 text-white/40 hover:text-white/80 transition-colors">
            <ChevronDown className="w-5 h-5" />
          </motion.div>
        </motion.button>
      </div>

      {/* Spacer to give scroll room for the fly-through effect */}
      <div className="h-[30vh] pointer-events-none" aria-hidden="true" />
    </section>
  );
};

export default HeroSection;
